import { useCallback, useEffect, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail, nivelInfo } from "@/lib/api";
import { emocionInfo } from "@/lib/emociones";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { NivelAlumnoSelect } from "@/components/NivelAlumnoSelect";
import { toast } from "sonner";
import { ImagePlus, Loader2, RotateCcw, ArrowLeft, Trash2 } from "lucide-react";

/** Ficha del alumno para el profesor: nivel, emoción, recuperaciones y galería de trabajos. */
export default function FichaProfesor() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [ficha, setFicha] = useState(null);
  const [error, setError] = useState("");
  const [avances, setAvances] = useState([]);
  const [open, setOpen] = useState(params.get("subir") === "1");
  const [subiendo, setSubiendo] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [avance, setAvance] = useState({ titulo: "", comentario: "", file_id: null });

  const load = useCallback(() => {
    api.get(`/alumnos/${id}/ficha-profesor`)
      .then(({ data }) => { setFicha(data); setError(""); })
      .catch((e) => setError(e.response?.status === 404
        ? "No puedes ver la ficha de este alumno: no pertenece a tus sedes. Pide a administración que revise tu ficha de profesor."
        : formatApiErrorDetail(e.response?.data?.detail) || "No se ha podido cargar la ficha."));
    api.get(`/alumnos/${id}/avances`).then(({ data }) => setAvances(data)).catch(() => {});
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const subir = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSubiendo(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setAvance((a) => ({ ...a, file_id: data.file_id }));
      toast.success("Foto lista");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubiendo(false);
    }
  };

  const guardar = async () => {
    setGuardando(true);
    try {
      await api.post(`/alumnos/${id}/avances`, avance);
      toast.success("Trabajo publicado: la familia ya puede verlo");
      setAvance({ titulo: "", comentario: "", file_id: null });
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setGuardando(false);
    }
  };

  const borrar = async (aid) => {
    await api.delete(`/avances/${aid}`).catch(() => {});
    load();
  };

  if (error) return (
    <Card className="rounded-2xl p-6 border-destructive/40 bg-destructive/10" data-testid="ficha-error">
      <p className="font-display font-bold text-destructive">No se puede abrir la ficha</p>
      <p className="text-sm text-muted-foreground mt-2">{error}</p>
      <Link to="/admin/pasar-lista">
        <Button variant="outline" className="rounded-full font-semibold mt-5" data-testid="ficha-error-volver">
          <ArrowLeft className="h-4 w-4 mr-2" /> Volver a la lista
        </Button>
      </Link>
    </Card>
  );
  if (!ficha) return <Loader2 className="h-5 w-5 animate-spin text-primary" />;

  const nivel = nivelInfo(ficha.nivel);
  const emocion = emocionInfo(ficha.emocion);

  return (
    <div data-testid="ficha-profesor-page">
      <PageHeader
        title={`${ficha.nombre} ${ficha.apellidos}`.trim()}
        subtitle={`${ficha.matricula} · ${ficha.clases.join(", ") || "sin clase asignada"}`}
        action={
          <Link to="/admin/pasar-lista">
            <Button variant="outline" className="rounded-full font-semibold" data-testid="volver-lista-btn">
              <ArrowLeft className="h-4 w-4 mr-2" /> Volver a la lista
            </Button>
          </Link>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="rounded-2xl p-6 border-border shadow-sm" data-testid="ficha-nivel">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Nivel</p>
          <p className="font-display text-2xl font-extrabold mt-3 flex items-center gap-2">
            <span className={`h-3 w-3 rounded-full ${nivel.dot}`} /> {nivel.label}
          </p>
          <div className="mt-4">
            <NivelAlumnoSelect alumno={{ ...ficha, id }} esProfesor onCambio={load} />
          </div>
        </Card>

        <Card className="rounded-2xl p-6 border-border shadow-sm" data-testid="ficha-emocion">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Cómo llegó</p>
          {ficha.emocion ? (
            <>
              <p className="font-display text-2xl font-extrabold mt-3">
                <span className="text-3xl mr-2">{emocion.emoji}</span>{emocion.label}
              </p>
              <p className="text-xs text-muted-foreground mt-2">Registrado el {ficha.emocion_fecha}</p>
            </>
          ) : (
            <p className="text-sm text-muted-foreground mt-3">
              Todavía no hay registro emocional del check-in.
            </p>
          )}
        </Card>

        <Card className="rounded-2xl p-6 border-border shadow-sm" data-testid="ficha-reposiciones">
          <div className="flex items-center gap-2">
            <RotateCcw className="h-4 w-4 text-primary" />
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Clases a recuperar</p>
          </div>
          <p className="font-display text-3xl font-extrabold mt-3">{ficha.reposiciones_pendientes.length}</p>
          <div className="mt-3 space-y-1">
            {ficha.reposiciones_pendientes.map((r) => (
              <p key={r.id} className="text-xs text-muted-foreground" data-testid={`ficha-repo-${r.id}`}>
                Falta del {r.fecha_origen}{r.caduca ? ` · válida hasta ${r.caduca}` : ""}
              </p>
            ))}
            {ficha.reposiciones_pendientes.length === 0 && (
              <p className="text-xs text-muted-foreground">No tiene clases pendientes de recuperar.</p>
            )}
          </div>
          {ficha.seguro_reposicion && (
            <Badge variant="secondary" className="mt-4 rounded-full">Con seguro de recuperación</Badge>
          )}
        </Card>
      </div>

      {(ficha.turnos?.length > 0 || ficha.clases_disponibles >= 0) && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          <Card className="rounded-2xl p-6 border-border shadow-sm" data-testid="ficha-turnos">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Su horario</p>
            <div className="mt-3 space-y-1.5">
              {(ficha.turnos || []).map((t, i) => (
                <p key={i} className="text-sm font-semibold" data-testid={`ficha-turno-${i}`}>
                  {t.dia} · {t.hora_inicio}{t.hora_fin ? `–${t.hora_fin}` : ""}
                  <span className="text-muted-foreground font-normal"> · {t.clase}</span>
                </p>
              ))}
              {(ficha.turnos || []).length === 0 && (
                <p className="text-sm text-muted-foreground">
                  Sin turno concreto asignado: viene a {ficha.clases.join(", ") || "su clase"}.
                </p>
              )}
            </div>
          </Card>
          <Card className="rounded-2xl p-6 border-border shadow-sm" data-testid="ficha-bono">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Clases disponibles</p>
            <p className="font-display text-3xl font-extrabold mt-3">{ficha.clases_disponibles}</p>
            <p className="text-xs text-muted-foreground mt-2">
              {ficha.seguro_reposicion ? "Tiene seguro de recuperación de clases." : "Sin seguro de recuperación."}
              {ficha.bono_hasta ? ` Bono válido hasta ${ficha.bono_hasta}.` : ""}
            </p>
          </Card>
        </div>
      )}

      {ficha.observaciones && (
        <Card className="rounded-2xl p-6 mt-6 border-border shadow-sm" data-testid="ficha-observaciones">
          <p className="text-xs uppercase tracking-widest text-muted-foreground">Observaciones</p>
          <p className="text-sm mt-3">{ficha.observaciones}</p>
        </Card>
      )}

      <div className="flex items-center justify-between gap-4 mt-10 mb-5">
        <h2 className="font-display text-2xl font-extrabold">Sus trabajos</h2>
        <Button className="rounded-full font-semibold" onClick={() => setOpen(true)} data-testid="ficha-add-avance-btn">
          <ImagePlus className="h-4 w-4 mr-2" /> Subir trabajo
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {avances.map((a) => (
          <Card key={a.id} className="rounded-2xl overflow-hidden border-border shadow-sm" data-testid={`ficha-avance-${a.id}`}>
            {a.file_id && (
              <img src={`${process.env.REACT_APP_BACKEND_URL}/api/files/${a.file_id}`} alt={a.titulo}
                className="w-full h-44 object-cover" />
            )}
            <div className="p-5">
              <div className="flex items-start justify-between gap-2">
                <p className="font-display font-bold">{a.titulo}</p>
                <button onClick={() => borrar(a.id)} data-testid={`ficha-avance-borrar-${a.id}`}>
                  <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive transition-colors duration-200" />
                </button>
              </div>
              <p className="text-sm text-muted-foreground mt-2">{a.comentario}</p>
              <p className="text-xs text-muted-foreground mt-3">{a.autor}</p>
            </div>
          </Card>
        ))}
        {avances.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="ficha-sin-avances">
            Todavía no hay trabajos publicados de este alumno.
          </p>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-2xl max-h-[85vh] overflow-y-auto" data-testid="ficha-avance-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Subir trabajo</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label>Título</Label>
              <Input value={avance.titulo} data-testid="ficha-avance-titulo"
                onChange={(e) => setAvance({ ...avance, titulo: e.target.value })} />
            </div>
            <div className="grid gap-2">
              <Label>Comentario para la familia</Label>
              <Textarea value={avance.comentario} data-testid="ficha-avance-comentario"
                placeholder="Ha trabajado la mezcla de colores y el sombreado…"
                onChange={(e) => setAvance({ ...avance, comentario: e.target.value })} />
            </div>
            <div className="grid gap-2">
              <Label>Foto</Label>
              <Input type="file" accept="image/*" onChange={subir} data-testid="ficha-avance-file" />
              {subiendo && <Loader2 className="h-4 w-4 animate-spin text-primary" />}
              {avance.file_id && <p className="text-xs text-primary font-semibold">Foto subida</p>}
            </div>
          </div>
          <DialogFooter>
            <Button className="rounded-full font-semibold" onClick={guardar} data-testid="ficha-avance-guardar"
              disabled={guardando || !avance.titulo.trim()}>
              {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : "Publicar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
